// src/features/hoa-admin/screens/DashboardPage.tsx
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { usePolling, getPollingQueryOpts } from "../layout/HoaAdminLayout";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { getDashboardStats } from "@/api/reports";
import { listBuildings } from "@/api/buildings";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Info,
  Building2,
  Users as UsersIconLucide,
  Wrench,
  CreditCard,
  CalendarDays,
  Megaphone,
  BarChart3,
} from "lucide-react";

/* ------------------ types for still-mocked sections ------------------ */
type Announcement = {
  id: string;
  title: string;
  message: string;
  date: string;
  author: string;
  type: "general" | "maintenance" | "meeting" | "policy" | "amenity";
  priority: "low" | "medium" | "high";
};
type Poll = {
  id: string;
  title: string;
  status: "SCHEDULED" | "LIVE" | "CLOSED";
  endAt?: string;
  options: { id: string; label: string; votes: number }[];
};

const MOCK_ANNOUNCEMENTS: Announcement[] = [
  {
    id: "a101",
    title: "hoa.dashboard.mock.announcements.a101.title",
    message: "hoa.dashboard.mock.announcements.a101.message",
    date: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    author: "roles.hoaAdmin",
    type: "maintenance",
    priority: "high",
  },
  {
    id: "a102",
    title: "hoa.dashboard.mock.announcements.a102.title",
    message: "hoa.dashboard.mock.announcements.a102.message",
    date: new Date(Date.now() - 1000 * 60 * 60 * 26).toISOString(),
    author: "roles.hoaAdmin",
    type: "meeting",
    priority: "medium",
  },
  {
    id: "a103",
    title: "hoa.dashboard.mock.announcements.a103.title",
    message: "hoa.dashboard.mock.announcements.a103.message",
    date: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
    author: "roles.hoaAdmin",
    type: "amenity",
    priority: "low",
  },
];

let MOCK_POLLS: Poll[] = [
  {
    id: "p201",
    title: "hoa.dashboard.mock.polls.p201.title",
    status: "LIVE",
    endAt: new Date(Date.now() + 1000 * 60 * 60 * 18).toISOString(),
    options: [
      { id: "o1", label: "common.yes", votes: 12 },
      { id: "o2", label: "common.no", votes: 5 },
    ],
  },
  {
    id: "p202",
    title: "hoa.dashboard.mock.polls.p202.title",
    status: "SCHEDULED",
    endAt: new Date(Date.now() + 1000 * 60 * 60 * 48).toISOString(),
    options: [
      { id: "o1", label: "hoa.dashboard.mock.polls.p202.options.approve", votes: 0 },
      { id: "o2", label: "hoa.dashboard.mock.polls.p202.options.reject", votes: 0 },
    ],
  },
];

async function apiRecentAnnouncements(): Promise<Announcement[]> {
  await sleep(120);
  return MOCK_ANNOUNCEMENTS.sort(
    (a, b) => +new Date(b.date) - +new Date(a.date)
  ).slice(0, 3);
}
async function apiActivePolls(): Promise<Poll[]> {
  await sleep(120);
  // auto-close on read if expired
  MOCK_POLLS = MOCK_POLLS.map((p) =>
    p.status !== "CLOSED" && p.endAt && Date.now() > new Date(p.endAt).getTime()
      ? { ...p, status: "CLOSED" }
      : p
  );
  const order = { LIVE: 0, SCHEDULED: 1, CLOSED: 2 } as const;
  return MOCK_POLLS.slice()
    .sort((a, b) => order[a.status] - order[b.status])
    .filter((p) => p.status !== "CLOSED")
    .slice(0, 2);
}
function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

/* ------------------ design tokens / helpers ------------------ */
const PRIMARY = "#1F3A93";
/** Quiet outline style (consistent with other pages) */
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50";

/* ----------------------------- Page ----------------------------- */
export default function DashboardPage() {
  const nav = useNavigate();
  const { enabled, intervalMs } = usePolling();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [buildingId, setBuildingId] = React.useState<string>("");
  const locale = language === "es" ? "es-ES" : "en-US";
  const usdFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [locale]
  );

  const condosQuery = useQuery({
    queryKey: ["hoa-admin", "buildings"],
    queryFn: listBuildings,
    ...getPollingQueryOpts(false, 0),
  });
  const condos = condosQuery.data || [];

  // Live data from backend
  const dashStats = useQuery({
    queryKey: ["hoa-admin", "dashboard", "stats", buildingId],
    queryFn: () => getDashboardStats(buildingId || undefined),
    ...getPollingQueryOpts(enabled, intervalMs),
  });

  // Still mock (announcements/polls have no backend yet)
  const anns = useQuery({
    queryKey: ["hoa-admin", "dashboard", "announcements"],
    queryFn: apiRecentAnnouncements,
    ...getPollingQueryOpts(enabled, intervalMs),
  });
  const polls = useQuery({
    queryKey: ["hoa-admin", "dashboard", "polls"],
    queryFn: apiActivePolls,
    ...getPollingQueryOpts(enabled, intervalMs),
  });

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Greeting */}
      <Card variant="glass" className="rounded-3xl p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
              <Info className="w-5 h-5 text-gray-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-[18px] sm:text-[20px] font-semibold tracking-[-0.01em] text-gray-900">
                {t("hoa.dashboard.greeting", { name: user?.first_name || t("roles.hoaAdmin") })}
              </h2>
              <p className="text-gray-600 mt-1 text-sm">
                {t("hoa.dashboard.subtitle")}
              </p>
            </div>
          </div>
          {/* Building Selector */}
          <div className="flex-shrink-0 w-full sm:w-64">
            <select
              className="w-full text-base font-medium text-gray-800 border-0 bg-white ring-1 shadow-sm ring-black/10 rounded-2xl px-4 py-3 cursor-pointer hover:ring-[#1F3A93]/50 focus:ring-2 focus:ring-[#1F3A93] transition-all outline-none appearance-none"
              style={{
                backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1.25em 1.25em',
                paddingRight: '2.5rem'
              }}
              value={buildingId}
              onChange={(e) => setBuildingId(e.target.value)}
            >
              <option value="">{t("common.allCondos")}</option>
              {condos.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Overview (uses Stat) */}
      <Card
        variant="glass"
        rail
        railColor="border-blue-500"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <h3 className="font-semibold text-lg tracking-tight text-gray-900">
          {t("hoa.dashboard.overview")}
        </h3>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat
            title={t("hoa.dashboard.units")}
            value={dashStats.isLoading ? "…" : dashStats.data?.counts.units ?? "-"}
            icon={<Building2 className="w-4 h-4" />}
            tone="slate"
          />
          <Stat
            title={t("hoa.dashboard.residents")}
            value={dashStats.isLoading ? "…" : dashStats.data?.counts.residents ?? "-"}
            icon={<UsersIconLucide className="w-4 h-4" />}
            tone="slate"
          />
          <Stat
            title={t("hoa.dashboard.openIssues")}
            value={dashStats.isLoading ? "…" : dashStats.data?.counts.open_issues ?? "-"}
            icon={<Wrench className="w-4 h-4" />}
            tone="amber"
          />
          <Stat
            title={t("hoa.dashboard.pendingPayments")}
            value={dashStats.isLoading ? "…" : dashStats.data?.counts.pending_payments ?? "-"}
            icon={<CreditCard className="w-4 h-4" />}
            tone="rose"
          />
        </div>
      </Card>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <ActionTile
          title={t("hoa.dashboard.quick.create")}
          subtitle={t("hoa.dashboard.quick.announcement")}
          icon={<Megaphone className="w-6 h-6" />}
          onClick={() => nav("/hoa-admin/communications?tab=announcements")}
        />
        <ActionTile
          title={t("hoa.dashboard.quick.start")}
          subtitle={t("hoa.dashboard.quick.poll")}
          icon={<BarChart3 className="w-6 h-6" />}
          onClick={() => nav("/hoa-admin/communications?tab=polling")}
        />
        <ActionTile
          title={t("hoa.dashboard.quick.review")}
          subtitle={t("hoa.dashboard.quick.payments")}
          icon={<CreditCard className="w-6 h-6" />}
          onClick={() => nav("/hoa-admin/payments")}
        />
        <ActionTile
          title={t("hoa.dashboard.quick.log")}
          subtitle={t("hoa.dashboard.quick.issue")}
          icon={<Wrench className="w-6 h-6" />}
          onClick={() => nav("/hoa-admin/issues")}
        />
      </div>

      {/* Recent Announcements */}
      <div className="relative">
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 rounded-3xl backdrop-blur-sm bg-white/60">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white/90 ring-1 ring-black/10 shadow-xl px-8 py-7 text-center max-w-xs mx-4">
            <span className="text-4xl">🚧</span>
            <h2 className="text-xl font-semibold text-gray-900">{t("common.comingSoon")}</h2>
            <p className="text-sm text-gray-500 leading-relaxed">{t("hoa.dashboard.announcementsComing")}</p>
            <span className="inline-block rounded-full bg-[#1F3A93]/10 text-[#1F3A93] text-xs font-medium px-3 py-1">{t("common.inDevelopment")}</span>
          </div>
        </div>
        <div className="blur-[2px] pointer-events-none select-none">
      <Card
        variant="glass"
        rail
        railColor="border-amber-500"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Megaphone className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("hoa.dashboard.recentAnnouncements")}
                </h3>
                <Badge className="bg-white text-black ring-1 ring-black/10">
                  {t("hoa.dashboard.shown", { count: (anns.data ?? []).length })}
                </Badge>
              </div>
              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" /> {t("hoa.dashboard.updatedJustNow")}
              </div>
            </div>

            <div className="mt-3">
              {anns.isLoading ? (
                <ListSkeleton rows={3} />
              ) : (anns.data ?? []).length === 0 ? (
                <EmptyList text={t("hoa.dashboard.noAnnouncements")} />
              ) : (
                <ul className="space-y-2">
                  {anns.data!.map((a) => (
                    <li
                      key={a.id}
                      className="rounded-xl bg-white/80 backdrop-blur ring-1 ring-black/5 px-4 py-3"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="rounded-2xl p-2"
                          style={{
                            background: getTypeBg(a.type),
                            color: getTypeFg(a.type),
                          }}
                        >
                          <Megaphone className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="truncate text-[15px] font-semibold text-gray-900">
                              {t(a.title)}
                            </h4>
                            <span className="shrink-0 text-xs text-gray-600">
                              {new Date(a.date).toLocaleDateString(locale)}
                            </span>
                          </div>
                          <p className="mt-1 line-clamp-2 text-sm text-gray-700/90">
                            {t(a.message)}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  className={`rounded-2xl px-4 py-2.5 ${QUIET}`}
                  onClick={() =>
                    nav("/hoa-admin/communications?tab=announcements")
                  }
                >
                  {t("hoa.dashboard.viewAll")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
        </div>
      </div>

      {/* Active Polls */}
      {/* Active Polls */}
      <div className="relative">
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 rounded-3xl backdrop-blur-sm bg-white/60">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white/90 ring-1 ring-black/10 shadow-xl px-8 py-7 text-center max-w-xs mx-4">
            <span className="text-4xl">🚧</span>
            <h2 className="text-xl font-semibold text-gray-900">{t("common.comingSoon")}</h2>
            <p className="text-sm text-gray-500 leading-relaxed">{t("hoa.dashboard.pollsComing")}</p>
            <span className="inline-block rounded-full bg-[#1F3A93]/10 text-[#1F3A93] text-xs font-medium px-3 py-1">{t("common.inDevelopment")}</span>
          </div>
        </div>
        <div className="blur-[2px] pointer-events-none select-none">
      <Card
        variant="glass"
        rail
        railColor="border-blue-500"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <BarChart3 className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("hoa.dashboard.activePolls")}
                </h3>
                <Badge className="bg-white text-black ring-1 ring-black/10">
                  {t("hoa.dashboard.shown", { count: (polls.data ?? []).length })}
                </Badge>
              </div>
              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" /> {t("hoa.dashboard.liveScheduled")}
              </div>
            </div>

            <div className="mt-3">
              {polls.isLoading ? (
                <ListSkeleton rows={2} />
              ) : (polls.data ?? []).length === 0 ? (
                <EmptyList text={t("hoa.dashboard.noPolls")} />
              ) : (
                <div className="space-y-2">
                  {polls.data!.map((p) => {
                    const total = p.options.reduce(
                      (a, b) => a + (b.votes ?? 0),
                      0
                    );
                    return (
                      <div
                        key={p.id}
                        className="rounded-xl bg-white/80 backdrop-blur ring-1 ring-black/5 px-4 py-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                                style={
                                  p.status === "LIVE"
                                    ? {
                                        background: "rgba(39,174,96,0.12)",
                                        color: "#166534",
                                      }
                                    : {
                                        background: "rgba(245,158,11,0.12)",
                                        color: "#78350F",
                                      }
                                }
                              >
                                {p.status === "LIVE"
                                  ? t("hoa.dashboard.status.live")
                                  : t("hoa.dashboard.status.scheduled")}
                              </span>
                              {p.endAt && (
                                <span className="text-xs text-gray-600">
                                  {t("hoa.dashboard.ends", {
                                    date: new Date(p.endAt).toLocaleString(locale),
                                  })}
                                </span>
                              )}
                            </div>

                            <h4 className="mt-1 truncate text-[15px] font-semibold text-gray-900">
                              {t(p.title)}
                            </h4>

                            {/* Options: numbers + percentages only */}
                            <ul className="mt-2 divide-y divide-black/5">
                              {p.options.map((o) => {
                                const pct = total
                                  ? Math.round((o.votes / total) * 100)
                                  : 0;
                                return (
                                  <li key={o.id} className="py-1.5">
                                    <div className="flex items-center justify-between text-xs sm:text-[13px]">
                                      <span className="text-gray-900">
                                        {t(o.label)}
                                      </span>
                                      <span className="text-gray-600 tabular-nums">
                                        {o.votes} ({pct}%)
                                      </span>
                                    </div>
                                  </li>
                                );
                              })}
                              <li className="pt-1.5 text-[11px] text-gray-500">
                                {t("hoa.dashboard.totalVotes")}:{" "}
                                <span className="tabular-nums">{total}</span>
                              </li>
                            </ul>
                          </div>

                          <Button
                            variant="outline"
                            className={`shrink-0 h-8 px-3 ${QUIET}`}
                            onClick={() =>
                              nav("/hoa-admin/communications?tab=polling")
                            }
                          >
                            {t("hoa.dashboard.manage")}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  className={`rounded-2xl px-4 py-2.5 ${QUIET}`}
                  onClick={() => nav("/hoa-admin/communications?tab=polling")}
                >
                  {t("hoa.dashboard.openPolls")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
        </div>
      </div>

      {/* Issues snapshot */}
      <Card
        variant="glass"
        rail
        railColor="border-amber-500"
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <Wrench className="w-5 h-5 text-gray-700" />
          </div>
          {/* ///////////////// */}

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("hoa.dashboard.issues")}
                </h3>

                {!dashStats.isLoading && dashStats.data && (
                  <Badge className="bg-white text-black ring-1 ring-black/10">
                    {t("hoa.dashboard.openCount", {
                      count: dashStats.data.counts.open_issues,
                    })}
                  </Badge>
                )}
              </div>

              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" /> {t("hoa.dashboard.updatedJustNow")}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-2">
          {dashStats.isLoading ? (
            <div className="mt-3">
              <ListSkeleton rows={2} />
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="rounded-xl bg-gray-50 ring-1 ring-black/5 px-4 py-3 text-sm text-gray-600">
                {t("hoa.dashboard.openCount", { count: dashStats.data?.counts.open_issues ?? 0 })}
              </div>
            </div>
          )}

          <div className="mt-2 flex justify-end">
            <Button
              variant="outline"
              className={`rounded-2xl px-4 py-2.5 ${QUIET}`}
              onClick={() => nav("/hoa-admin/issues")}
            >
              {t("hoa.dashboard.openBoard")}
            </Button>
          </div>
        </div>
      </Card>

      {/* Payments snapshot */}
      <Card
        variant="glass"
        rail
        railColor={
          (dashStats.data?.payments_snapshot.pending_proofs ?? 0) > 0
            ? "border-amber-500"
            : "border-emerald-500"
        }
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-2xl p-2.5 bg-white ring-1 ring-black/10 shadow-sm">
            <CreditCard className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                  {t("hoa.dashboard.payments")}
                </h3>
                {!dashStats.isLoading && dashStats.data && (
                  <Badge className="bg-white text-black ring-1 ring-black/10">
                    {t("hoa.dashboard.pendingProofs", {
                      count: dashStats.data.payments_snapshot.pending_proofs,
                    })}
                  </Badge>
                )}
              </div>
              <div className="sm:ml-auto inline-flex items-center gap-1 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4" /> {t("hoa.dashboard.thisMonth")}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2">
          {dashStats.isLoading ? (
            <div className="mt-3">
              <ListSkeleton rows={2} />
            </div>
          ) : dashStats.data ? (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <div className="rounded-2xl px-4 py-3 text-sm bg-gray-50 ring-1 ring-black/5">
                <div className="font-medium text-gray-900">
                  {t("hoa.dashboard.approvedThisWeek")}
                </div>
                <div className="text-gray-700/80">
                  {dashStats.data.payments_snapshot.approved_this_month}
                </div>
              </div>
              <div className="rounded-2xl px-4 py-3 text-sm bg-gray-50 ring-1 ring-black/5">
                <div className="font-medium text-gray-900">
                  {t("hoa.dashboard.rejectedThisWeek")}
                </div>
                <div className="text-gray-700/80">
                  {dashStats.data.payments_snapshot.rejected_this_month}
                </div>
              </div>
              <div className="sm:col-span-2 rounded-2xl px-4 py-3 text-sm bg-gray-50 ring-1 ring-black/5">
                <div className="font-medium text-gray-900">
                  {t("hoa.dashboard.collectedThisMonth")}
                </div>
                <div className="text-gray-900 text-[18px] font-semibold">
                  {usdFormatter.format(
                    dashStats.data.payments_snapshot.total_collected_this_month
                  )}
                </div>
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <Button
                  className="rounded-2xl px-3 py-2 h-auto text-white"
                  style={{ backgroundColor: PRIMARY }}
                  onClick={() => nav("/hoa-admin/payments")}
                >
                  {t("hoa.dashboard.reviewPayments")}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

/* --------------------- Reusable pieces --------------------- */
function ListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-xl bg-white/70 backdrop-blur ring-1 ring-black/5 px-4 py-3"
        >
          <div className="h-3.5 w-1/2 rounded bg-gray-200" />
          <div className="mt-2 h-3 w-2/3 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}
function EmptyList({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-dashed border-black/10 bg-white/60 backdrop-blur p-6 text-center text-sm text-gray-600">
      {text}
    </div>
  );
}
function Bar({
  label,
  value,
  max,
  tone,
}: {
  label: string;
  value: number;
  max: number;
  tone?: "amber" | "blue" | "green";
}) {
  const pct = Math.min(100, Math.round((value / (max || 1)) * 100));
  const color =
    tone === "amber" ? "#F59E0B" : tone === "blue" ? "#1F3A93" : "#27AE60";
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-gray-900">{label}</span>
        <span className="text-gray-600">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-[#EEF2FF]">
        <div
          className="h-2 rounded-full"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

/* ---- ActionTile (Apple-ish quick action) ---- */
function ActionTile({
  title,
  subtitle,
  icon,
  onClick,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={`${title} ${subtitle}`}
      className={[
        "rounded-2xl text-left transition will-change-transform",
        "bg-white/80 backdrop-blur-xl ring-1 ring-black/5 shadow-lg shadow-black/10 hover:shadow-xl",
        "active:scale-[0.99] px-4 py-4 flex items-center gap-3",
      ].join(" ")}
    >
      <div className="shrink-0 rounded-2xl p-2 bg-white ring-1 ring-black/10 shadow-sm">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-600">{subtitle}</div>
      </div>
    </button>
  );
}

/* ---- NEW: Stat (for Overview KPIs) ---- */
function Stat({
  title,
  value,
  icon,
  tone = "slate",
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  tone?: "slate" | "emerald" | "amber" | "rose";
}) {
  const tones: Record<string, string> = {
    slate: "bg-gray-50 ring-gray-200 text-gray-900",
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-900",
    amber: "bg-amber-50 ring-amber-200 text-amber-900",
    rose: "bg-rose-50 ring-rose-200 text-rose-900",
  };
  return (
    <div className={`rounded-2xl px-4 py-3 ring-1 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-sm">
        <span className="grid place-items-center rounded-lg bg-white/80 ring-1 ring-black/5 p-1.5">
          {icon}
        </span>
        <span className="font-medium">{title}</span>
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

/* ---- style helpers for announcement chips (unchanged) ---- */
function getTypeBg(t: Announcement["type"]) {
  switch (t) {
    case "maintenance":
      return "rgba(254,215,170,0.35)";
    case "meeting":
      return "rgba(199,210,254,0.45)";
    case "policy":
      return "rgba(254,202,202,0.40)";
    case "amenity":
      return "rgba(187,247,208,0.45)";
    case "general":
    default:
      return "rgba(229,231,235,0.6)";
  }
}
function getTypeFg(t: Announcement["type"]) {
  switch (t) {
    case "maintenance":
      return "#9A3412";
    case "meeting":
      return "#1E3A8A";
    case "policy":
      return "#991B1B";
    case "amenity":
      return "#166534";
    case "general":
    default:
      return "#374151";
  }
}
